import { Badge, Box, Group, Stack, useMantineTheme } from '@mantine/core';
import {
  IconBuildingSkyscraper,
  IconClockHour4,
  IconFile,
  IconMapPinPlus,
  IconUser,
  IconWorldPlus,
} from '@tabler/icons-react';
import FileLink from 'components/files/FileLink';
import DateDisplay from 'components/DateDisplay';
import BadgeDeleted from 'components/Badge';
import DeviceImage from 'components/DeviceImage';
import OrganizationList from 'components/OrganizationList';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import P2Medium from 'components/typography/P2Medium';
import P3Regular from 'components/typography/P3Regular';
import DepartmentBreadcrumb from 'components/department/DepartmentBreadcrumb';
import { ReactNode } from 'react';
import {
  formatHrDeadlineState,
  formatHrRecurrence,
  formatHrStatus,
  formatHrTermKind,
  resolveHrDeadlineState,
} from 'pages/hr-module/hr-formatters';

interface CellProps {
  value?: any;
  data?: any;
}

/** Renders an authorized primary photo with the shared device-image presentation and viewer. */
function HrPrimaryPhoto({ fileId }: { fileId?: string }) {
  return <DeviceImage imageFiles={fileId ? [{ fileId, isMainImage: true }] : []} />;
}

/** Returns whether an explicitly retained row is no longer active. */
function isRetainedRow(data: any) {
  const status = data?.rawStatus ?? data?.status;
  return (
    status === false ||
    status === 0 ||
    status === '0' ||
    ['inactive', 'archived', 'completed', 'removed'].includes(status)
  );
}

/** Returns whether a term is archived and should carry the deleted-row badge. */
function isArchivedTerm(data: any) {
  return (data?.rawStatus ?? data?.status) === 'archived';
}

/** Renders a table value and leaves an empty value blank. */
export function HrTextCell({ value }: CellProps) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return <P2Regular>{String(value)}</P2Regular>;
}

/** Renders canonical table identifiers. */
export function HrIdCell({ value }: CellProps) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return <P3Regular>{value}</P3Regular>;
}

/** Renders an icon-and-label reference shared by HR relation columns. */
function HrReferenceCell({ icon, label, badgeText }: { icon: ReactNode; label?: string; badgeText?: string }) {
  if (!label) {
    return null;
  }

  return (
    <Group spacing={8} noWrap align="center">
      {badgeText && <BadgeDeleted text={badgeText} />}
      <Box w={24} h={24}>
        {icon}
      </Box>
      <P2Regular color="gray.8">{label}</P2Regular>
    </Group>
  );
}

/** Renders an organization reference. */
export function HrOrganizationCell({ data }: CellProps) {
  return <HrReferenceCell icon={<IconBuildingSkyscraper stroke={1.5} />} label={data?.organizationName} />;
}

/** Renders a department reference. */
export function HrDepartmentCell({ data }: CellProps) {
  if (!data?.departmentName) return null;
  return (
    <Group spacing={8} noWrap>
      <Box w={24} h={24}>
        <IconMapPinPlus stroke={1.5} />
      </Box>
      <DepartmentBreadcrumb breadcrumbs={data?.departmentBreadcrumbs ?? []} compact />
    </Group>
  );
}

/** Renders a user reference. */
export function HrUserCell({ data }: CellProps) {
  return <HrReferenceCell icon={<IconUser stroke={1.5} />} label={data?.userFullName} />;
}

/** Renders a person reference. */
export function HrPersonReferenceCell({ data }: CellProps) {
  return (
    <HrReferenceCell
      icon={<IconUser stroke={1.5} />}
      label={`${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim()}
      badgeText={isArchivedTerm(data) ? 'Vyradený' : undefined}
    />
  );
}

/** Renders a term-type reference. */
export function HrTermTypeCell({ data }: CellProps) {
  return <HrReferenceCell icon={<IconClockHour4 stroke={1.5} />} label={data?.termTypeName} />;
}

/** Renders a file-category reference. */
export function HrFileCategoryCell({ data }: CellProps) {
  return <HrReferenceCell icon={<IconFile stroke={1.5} />} label={data?.categoryName} />;
}

/** Renders a date-only table value through the shared application date component. */
export function HrDateCell({ value }: CellProps) {
  if (!value) {
    return null;
  }

  const date = new Date(typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
  return <DateDisplay date={date} />;
}

/** Renders a wrapped long-text value. */
export function HrLongTextCell({ value }: CellProps) {
  if (!value) {
    return null;
  }

  return <P2Regular style={{ whiteSpace: 'pre-wrap' }}>{value}</P2Regular>;
}

/** Renders the first-name column and marks retained people. */
export function HrPersonNameCell({ data, value }: CellProps) {
  return (
    <Group spacing={8} noWrap>
      <HrPrimaryPhoto fileId={data?.primaryPhotoFileId} />
      {isRetainedRow(data) && <BadgeDeleted text="Vyradená" />}
      <P2Regular style={{ opacity: isRetainedRow(data) ? 0.6 : 1 }}>{value}</P2Regular>
    </Group>
  );
}

/** Renders a configuration name and marks archived definitions. */
export function HrConfigurationNameCell({ data, value }: CellProps) {
  return (
    <Group spacing={8} noWrap>
      {isRetainedRow(data) && <BadgeDeleted text="Vyradené" />}
      <P2Regular style={{ opacity: isRetainedRow(data) ? 0.6 : 1 }}>{value}</P2Regular>
    </Group>
  );
}

/** Renders a person's complete identity for compact HR grids. */
export function HrPersonIdentityCell({ data }: CellProps) {
  return (
    <Group spacing={8} noWrap>
      <HrPrimaryPhoto fileId={data?.primaryPhotoFileId} />
      <Stack spacing={0} py={6}>
        <Group spacing={8} noWrap>
          {isRetainedRow(data) && <BadgeDeleted text="Vyradená" size="sm" />}
          <P1Regular>
            {data?.firstName} {data?.lastName}
          </P1Regular>
        </Group>
        <P2Regular>ID {data?.personId}</P2Regular>
      </Stack>
    </Group>
  );
}

/** Renders an HR lifecycle status consistently. */
export function HrStatusCell({ data, value }: CellProps) {
  const status = data?.rawStatus ?? value;
  const label = formatHrStatus(status);

  if (!label) {
    return null;
  }

  return <Badge color={status === 'active' || status === 'committed' ? 'green' : 'gray'}>{label}</Badge>;
}

/** Renders a term kind label. */
export function HrTermKindCell({ value }: CellProps) {
  return <HrTextCell value={formatHrTermKind(value)} />;
}

/** Renders a deadline state with urgency color. */
export function HrDeadlineCell({ data, value }: CellProps) {
  const deadlineState = resolveHrDeadlineState(value, data?.rawStatus ?? data?.status);
  const label = formatHrDeadlineState(deadlineState);

  if (!label) {
    return null;
  }

  const color = deadlineState === 'overdue' ? 'red' : deadlineState === 'completed' ? 'green' : 'orange';
  return <Badge color={color}>{label}</Badge>;
}

/** Renders a deadline state when present and otherwise the term lifecycle state. */
export function HrTermStateCell({ data }: CellProps) {
  const status = data?.rawStatus ?? data?.status;
  return data?.deadlineState ? <HrDeadlineCell value={data.deadlineState} /> : <HrStatusCell value={status} />;
}

/** Renders recurrence configuration. */
export function HrRecurrenceCell({ value }: CellProps) {
  return <HrTextCell value={formatHrRecurrence(value)} />;
}

/** Renders global or multi-organization configuration scope. */
export function HrScopeCell({ data }: CellProps) {
  const theme = useMantineTheme();
  if (data?.isGlobal) {
    return (
      <Group spacing={8} align="center">
        <IconWorldPlus stroke={2} color={theme.colors.red[8]} size={20} />
        <P2Medium color="gray.8" mt={4}>
          Všetky organizácie
        </P2Medium>
      </Group>
    );
  }

  return <OrganizationList organizations={data?.organizations ?? []} limit={2} />;
}

/** Renders a File Manager link without exposing an inaccessible identifier. */
export function HrFileLinkCell({ data }: CellProps) {
  return data?.fileId ? <FileLink fileId={data.fileId} /> : null;
}

/** Renders a compact term summary on narrow screens. */
export function HrTermSummaryCell({ data }: CellProps) {
  return (
    <Stack spacing={0} py={6}>
      <Group spacing={8} noWrap>
        {isArchivedTerm(data) && <BadgeDeleted text="Vyradený" size="sm" />}
        <P1Regular>
          {data?.firstName} {data?.lastName}
        </P1Regular>
      </Group>
      <P2Regular>{data?.termTypeName}</P2Regular>
      <Group spacing={8} align="center">
        <HrDateCell value={data?.nextDueDate} />
        <P2Regular>{formatHrDeadlineState(data?.deadlineState, data?.rawStatus ?? data?.status)}</P2Regular>
      </Group>
    </Stack>
  );
}
